/**
 * CameraShakeHelper — named camera-shake presets.
 *
 * Replaces the literal `cameras.main.shake(180, 0.006)` calls scattered
 * across every game with a small vocabulary of named intensities. Numbers
 * extracted from the actual usage in tap-tug, horse-dash, puff-up,
 * fruit-grab, balloon-pop, simon-says, smash-dash, shrink-sprint, etc.
 *
 * Usage:
 *   import { CameraShakeHelper } from '@platform/sdk';
 *
 *   // Named preset
 *   CameraShakeHelper.shake(this, 'tap');
 *
 *   // Combo tier (intensity grows with the tier index)
 *   CameraShakeHelper.combo(this, tier.index);
 *
 *   // Custom one-off
 *   CameraShakeHelper.shake(this, { duration: 220, magnitude: 0.012 });
 *
 * Presets:
 *   tap         — 120ms × 0.004  (light tap feedback)
 *   light       — 140ms × 0.005
 *   hit         — 180ms × 0.006  (combo tap, normal hit)
 *   strong      — 220ms × 0.010  (boss hit, heavy strike)
 *   big         — 260ms × 0.014  (round end, big explosion)
 *   crash       — 400ms × 0.020  (game over, catastrophic event)
 */

const PRESETS = Object.freeze({
  tap:    { duration: 120, magnitude: 0.004 },
  light:  { duration: 140, magnitude: 0.005 },
  hit:    { duration: 180, magnitude: 0.006 },
  strong: { duration: 220, magnitude: 0.010 },
  big:    { duration: 260, magnitude: 0.014 },
  crash:  { duration: 400, magnitude: 0.020 }
});

export class CameraShakeHelper {
  /**
   * Trigger a camera shake.
   * @param {Phaser.Scene} scene
   * @param {string|{duration:number, magnitude:number}} presetOrConfig
   *        Either the name of a built-in preset (`'tap'`, `'hit'`, ...) or
   *        a custom `{ duration, magnitude }` object. Unknown preset names
   *        fall back to `'hit'`.
   */
  static shake(scene, presetOrConfig) {
    if (!scene || !scene.cameras || !scene.cameras.main) return;
    let cfg;
    if (typeof presetOrConfig === 'string') {
      cfg = PRESETS[presetOrConfig] || PRESETS.hit;
    } else if (presetOrConfig && typeof presetOrConfig === 'object') {
      cfg = presetOrConfig;
    } else {
      cfg = PRESETS.hit;
    }
    try {
      scene.cameras.main.shake(cfg.duration, cfg.magnitude);
    } catch (_) { /* ignore */ }
  }

  /**
   * Tier-scaled shake — intensity grows with combo tier index. Mirrors the
   * formula horse-dash / puff-up / tap-tug all use:
   *   duration  = 120 + tierIndex * 30
   *   magnitude = 0.004 + tierIndex * 0.001
   * @param {Phaser.Scene} scene
   * @param {number} tierIndex   0..N
   */
  static combo(scene, tierIndex = 0) {
    if (!scene || !scene.cameras || !scene.cameras.main) return;
    const duration = 120 + tierIndex * 30;
    const magnitude = 0.004 + tierIndex * 0.001;
    try {
      scene.cameras.main.shake(duration, magnitude);
    } catch (_) { /* ignore */ }
  }

  /** All available preset names. */
  static get presets() {
    return Object.keys(PRESETS);
  }
}
